<template>
  <div :style="`position:relative;height:${height};width:100%`">
    <div :style="`width:100%;height:${height}`" :id="`virtualtour`"></div>
    <div
      class="controls text-white row items-start q-pt-md full-width justify-evenly"
    >
      <div class="col-2 q-pl-md">
        <q-icon
          class="cursor-pointer"
          name="collections"
          size="20px"
          color="white"
          @click.stop="togglePicker"
        />
      </div>
      <div
        v-if="componentData.data.cubes.length > 0"
        class="row col justify-center items-center"
      >
        <q-icon
          class="cursor-pointer q-pr-xs"
          @click="prevSlide"
          name="chevron_left"
          size="24px"
        />
        <div
          style="font-size: 14px; line-height: 18px; font-weight: 600"
          class="text-white"
        >
          {{ `${slide + 1}/${componentData.data.cubes.length}` }}
        </div>
        <q-icon
          class="cursor-pointer q-pl-xs"
          @click="nextSlide"
          name="chevron_right"
          size="24px"
        />
      </div>
      <div class="col-2 q-pl-md"></div>
    </div>
    <div :class="`text-container text-white q-pb-md q-px-md text-${textAlign}`">
      <div class="overline" v-if="overline">
        <div class="non-selectable text-uppercase">
          {{ overline }}
        </div>
      </div>
      <div class="title" v-if="title">
        <div class="non-selectable">
          {{ title }}
        </div>
      </div>
      <div class="caption" v-if="caption">
        <div class="non-selectable">
          {{ caption }}
        </div>
      </div>
    </div>
    <DFullScreenCarouselPicker
      @show="showPicker"
      @hide="hidePicker"
      ref="picker"
      @tileClick="tileClick"
      :behavior="behavior"
      :componentData="pickerComponentData"
      :height="height"
      class="full-width"
      v-if="pickerComponentData"
    />
  </div>
</template>

<script>
import mixin from '../../d-card-mixins'
import DFullScreenCarouselPicker from '../DFullScreenCarousel/DFullScreenCarouselPicker.vue'
const pannellum = window.pannellum
export default {
  name: 'DVirtualTourComponent',
  mixins: [mixin],
  props: {
    componentData: {
      type: Object,
      default: null
    },
    height: {
      type: String,
      required: true
    },
    behavior: {
      type: String,
      default: 'agent'
    }
  },
  data() {
    return {
      lslide: 0,
      viewer: null,
      pathStore: 'componentOverlay.vtstate',
      ignoreFirstAnimation: true,
      pickerShown: false,
      pickerComponentData: null
    }
  },
  computed: {
    controlHeight: {
      get() {
        return parseInt(this.height.replace('px', ''))
      }
    },
    position: {
      get() {
        if (this.behavior === 'consumer') {
          try {
            return JSON.parse(this.$cardStore.get(`${this.pathStore}.position`))
          } catch (e) {
            return {}
          }
        }
      },
      set(val) {
        if (this.behavior === 'consumer') {
          this.$cardStore.set(`${this.pathStore}.position`, JSON.stringify(val))
        }
      }
    },
    slide: {
      get() {
        if (this.behavior === 'consumer') {
          return this.$cardStore.get(`${this.pathStore}.slide`) || 0
        } else {
          return this.lslide
        }
      },
      set(val) {
        if (this.behavior === 'consumer') {
          this.$cardStore.set(`${this.pathStore}.slide`, val)
        } else {
          this.lslide = val
        }
      }
    },
    meetingPickerShown: {
      get() {
        if (this.behavior === 'consumer') {
          return this.$cardStore.get(`${this.pathStore}.pickerShown`)
        } else {
          return this.pickerShown
        }
      },
      set(val) {
        if (this.behavior === 'consumer') {
          this.$cardStore.set(`${this.pathStore}.pickerShown`, val)
        } else {
          this.pickerShown = val
        }
      }
    },
    currentCube: {
      get() {
        const cubes = this.componentData?.data?.cubes
        if (!cubes || !cubes[this.slide]) {
          return this.componentData?.data?.title
        }

        return cubes[this.slide]
      }
    },
    title: {
      get() {
        return this.currentCube?.title
      }
    },
    overline: {
      get() {
        return this.currentCube?.overline
      }
    },
    caption: {
      get() {
        return this.currentCube?.caption
      }
    },
    textAlign: {
      get() {
        return this.currentCube?.textAlign ?? 'center'
      }
    }
  },
  mounted() {
    if (this.$route.query.agent === 'true') {
      this.slide = 0
    }
    this.initializeSlide()
    this.setPicker()
    if (this.behavior === 'consumer') {
      this.$watch('position', (val) => {
        this.setPosition(val)
      })

      if (this.$route.query.agent === 'true') {
        this.position = { pitch: 0, yaw: 0, hfov: 100 }
      }
    }
    this.$watch('slide', (val) => {
      this.initializeSlide()
    })
  },
  methods: {
    prevSlide() {
      if (this.slide <= 1) return
      this.slide--
    },
    nextSlide() {
      if (this.slide >= this.componentData.data.cubes.length - 1) {
        this.slide = 0
        return
      }
      this.slide++
    },
    initializeSlide() {
      const baseUrl = this.componentData.baseUrl
      const cube = this.componentData.data.cubes[this.slide]
      const scene = {
        id: cube.id,
        type: 'cubemap',
        cubeMap: [
          baseUrl + cube.front + '?r=1',
          baseUrl + cube.right + '?r=1',
          baseUrl + cube.back + '?r=1',
          baseUrl + cube.left + '?r=1',
          baseUrl + cube.up + '?r=1',
          baseUrl + cube.down + '?r=1'
        ],
        friction: 0.5,
        showFullscreenCtrl: false,
        showControls: false,
        autoLoad: true
      }
      const scenes = {}
      scenes[cube.id] = scene
      this.ignoreFirstAnimation = true
      if (!this.viewer) {
        this.viewer = pannellum.viewer(`virtualtour`, {
          default: {
            firstScene: cube.id,
            sceneFadeDuration: 200,
            autoLoad: true,
            showControls: false,
            friction: 0.5,
            showFullscreenCtrl: false
          },
          scenes: scenes
        })
        if (this.viewer && this.behavior == 'consumer') {
          this.addAnimationListener()
        }
      } else {
        this.viewer.addScene(cube.id, scene)
        this.viewer.loadScene(cube.id)
      }
    },
    setPicker() {
      if (!this.componentData) {
        return
      }

      this.pickerComponentData = {
        ...this.componentData,
        data: {
          ...this.componentData.data,
          title: 'Virtual Tours',
          virtualtour: true,
          images: this.componentData.data.cubes.map((c, index) => ({
            type: 'image',
            title: c.title,
            caption: c.caption ?? null,
            overline: c.overline ?? null,
            src: c.front
          }))
        }
      }

    },
    addAnimationListener() {
      this.viewer.on('animatefinished', (event) => {
        if (this.ignoreFirstAnimation) {
          this.ignoreFirstAnimation = false
          //return;
        }
        this.position = {
          pitch: event.pitch,
          yaw: event.yaw,
          hfov: event.hfov
        }
      })
    },
    removeAnimationListener() {
      if (this.viewer) {
        this.viewer.off('animatefinished')
      }
    },
    setPosition(position) {
      if (this.viewer) {
        this.removeAnimationListener()
        const current = {
          pitch: this.viewer.getPitch(),
          yaw: this.viewer.getYaw(),
          hfov: this.viewer.getHfov()
        }
        if (JSON.stringify(position) !== JSON.stringify(current)) {
          this.viewer.setPitch(position.pitch, 150)
          this.viewer.setYaw(position.yaw, 150)
          this.viewer.setHfov(position.hfov, 150)
        }
        setTimeout(() => {
          this.addAnimationListener()
        }, 250)
      }
    },
    tileClick(index) {
      this.slide = index
    },
    togglePicker() {
      this.$refs.picker.toggle()
    },
    showPicker() {
      this.meetingPickerShown = true
    },
    hidePicker() {
      this.meetingPickerShown = false
    }
  },
  components: { DFullScreenCarouselPicker }
}
</script>
<style lang="scss" scoped>
.controls {
  width: 100%;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.3) 0%,
    rgba(0, 0, 0, 0) 100%
  );
  position: absolute;
  left: 0;
  top: 0;
  height: 60px;
}
.text-container {
  width: 100%;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.4) 100%
  );
  position: absolute;
  left: 0;
  bottom: 0;

  .overline {
    font-size: 9px;
    font-weight: 800;
    line-height: 16px;
    letter-spacing: 0.2px;
    opacity: 0.8;
  }
  .title {
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
    letter-spacing: -0.02px;
  }

  .caption {
    font-size: 14px;
    line-height: 18px;
    letter-spacing: -0.02px;
  }
}
</style>
